import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useMemo, useRef, useState } from 'react';
import '../../../styles/raid-strategy-site-alignment.css';
import { strategyBossDraftSourceKey } from '../domain/raidStrategyDraftSource.js';
import { buildTetrisCandidates } from '../domain/raidTetrisEngine.js';
import { useRaidTetris } from '../hooks/useRaidTetris.js';
import { RaidStrategyBoard } from './RaidStrategyBoard.jsx';
import { RaidTetrisBoardView } from './RaidTetrisBoard.jsx';

function candidateSelectionStorageKey(unionId, seasonNo) {
  return `dororaid:tetris-candidate-selection:${String(unionId || 'union')}:${Number(seasonNo || 0)}`;
}

function loadCandidateSelection(unionId, seasonNo) {
  if (typeof window === 'undefined') return { excludedMemberKeys: new Set(), excludedSlotIds: new Set() };
  try {
    const raw = window.localStorage.getItem(candidateSelectionStorageKey(unionId, seasonNo));
    const stored = raw ? JSON.parse(raw) : {};
    return {
      excludedMemberKeys: new Set(Array.isArray(stored.excludedMemberKeys) ? stored.excludedMemberKeys : []),
      excludedSlotIds: new Set(Array.isArray(stored.excludedSlotIds) ? stored.excludedSlotIds : []),
    };
  } catch {
    return { excludedMemberKeys: new Set(), excludedSlotIds: new Set() };
  }
}

export function RaidStrategyBoardPane({ tetris, mobile = false, embedded = false }) {
  const season = tetris.selectedSeason;
  const incomingBosses = Array.isArray(season?.bosses) ? season.bosses : [];
  const bossDraftSourceKey = strategyBossDraftSourceKey(incomingBosses);
  const stableBossesRef = useRef({ key: bossDraftSourceKey, bosses: incomingBosses });
  if (stableBossesRef.current.key !== bossDraftSourceKey) {
    stableBossesRef.current = { key: bossDraftSourceKey, bosses: incomingBosses };
  }
  const strategySeason = season ? { ...season, bosses: stableBossesRef.current.bosses } : season;

  const [recommendMode, setRecommendMode] = useState('nearest');
  const [preferAvailability, setPreferAvailability] = useState(false);
  const [currentAvailabilityOnly, setCurrentAvailabilityOnly] = useState(false);

  const candidates = useMemo(() => buildTetrisCandidates({
    members: season?.members || [],
    actualRecords: season?.records || [],
    practiceRecords: season?.practiceRecords || [],
    preferAvailability,
    currentAvailabilityOnly,
    ignoreStoredAvailability: !preferAvailability && !currentAvailabilityOnly,
  }), [season?.members, season?.practiceRecords, season?.records, preferAvailability, currentAvailabilityOnly]);

  // The placement comparison looks at one member's own registered decks, so it must not be narrowed
  // by the availability filter or by slots excluded from the Tetris candidate list.
  const placementCandidates = useMemo(() => buildTetrisCandidates({
    members: season?.members || [],
    actualRecords: season?.records || [],
    practiceRecords: season?.practiceRecords || [],
    ignoreStoredAvailability: true,
  }), [season?.members, season?.practiceRecords, season?.records]);

  const strategyCandidates = useMemo(() => {
    const selection = loadCandidateSelection(tetris.unionId, season?.seasonNo);
    return candidates.filter((candidate) => (
      !selection.excludedMemberKeys.has(`${Number(candidate.bossNo)}:${String(candidate.memberOpenid || '')}`)
      && !selection.excludedSlotIds.has(candidate.id)
    ));
  }, [candidates, season?.seasonNo, tetris.unionId]);

  return <RaidStrategyBoard season={strategySeason}
    candidates={strategyCandidates}
    placementCandidates={placementCandidates}
    viewerRole={tetris.viewerRole}
    mobile={mobile}
    embedded={embedded}
    recommendMode={recommendMode}
    onRecommendModeChange={setRecommendMode}
    preferAvailability={preferAvailability}
    onPreferAvailabilityChange={setPreferAvailability}
    currentAvailabilityOnly={currentAvailabilityOnly}
    onCurrentAvailabilityOnlyChange={setCurrentAvailabilityOnly}
  />;
}

export function RaidStrategyPage({ mobile = false }) {
  const [view, setView] = useState('recommend');
  const tetris = useRaidTetris();
  const season = tetris.selectedSeason;

  if (tetris.loading) return <LoadingState label="테트리스 데이터 동기화 중" variant="boss" count={5} />;
  if (tetris.errorMessage) return <section className={`raid-tetris-state error ${mobile ? 'mobile' : ''}`}><strong>테트리스 데이터를 불러오지 못했습니다.</strong><p>{tetris.errorMessage}</p><button type="button" onClick={() => tetris.refresh()}>다시 시도</button></section>;
  if (!season) return <section className={`raid-tetris-state ${mobile ? 'mobile' : ''}`}><strong>표시할 레이드 차수가 없습니다.</strong></section>;

  const handleRecommendClickCapture = (event) => {
    const button = event.target.closest?.('.raid-tetris-view-switch');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    setView('plan');
  };

  return (
    <section className={`raid-strategy-page ${mobile ? 'mobile' : 'desktop'}`}>
      {view === 'plan' ? (
        <>
          <div className="raid-tetris-view-head raid-tetris-plan-head">
            <h2>테트리스 계획표</h2>
            <div className="raid-tetris-view-actions">
              <button className="shared-view-switch raid-tetris-view-switch" type="button" onClick={() => setView('recommend')}>추천</button>
            </div>
          </div>
          <RaidStrategyBoardPane mobile={mobile} tetris={tetris} />
        </>
      ) : (
        <div onClickCapture={handleRecommendClickCapture}>
          <RaidTetrisBoardView mobile={mobile} tetris={tetris} />
        </div>
      )}
    </section>
  );
}
